import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/auth';
import { isUserAdmin } from '../../../utils/admin/adminUtils';
import adminApi from '../../../api/accounts/adminApi';
import { clearTokenCache } from '../../../api/core';
import { auth } from '../../../firebase/firebase';
import Footer from '../../../components/shared/Footer';
import { useToast } from '../../../components/ui/notifications/toast-notification.jsx';
import { LoadingSpinner, ButtonSpinner } from '../../../components/ui/feedback/loading-spinner.jsx';
import { ArrowLeft, Shield, Crown, Plus, Trash2, Mail, Hash } from 'lucide-react';
import { Input } from '../../../components/ui/inputs/input';
import { Button } from '../../../components/ui/buttons/button';
import { ConfirmationModal } from '../../../components/ui/modals/confirmation-modal.jsx';

export default function AdminClaims() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [uid, setUid] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [revokeTarget, setRevokeTarget] = useState(null);

  const isAdmin = useMemo(() => isUserAdmin(user), [user]);

  useEffect(() => {
    if (user && !isAdmin) navigate('/app');
  }, [user, isAdmin, navigate]);

  const loadAdmins = async () => {
    try {
      setIsLoading(true);
      const list = await adminApi.listAdmins();
      setAdmins(list);
      setError('');
    } catch (e) {
      setError(e.message || 'Failed to load admins');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) loadAdmins();
  }, [isAdmin]);

  const onGrant = async () => {
    if (!uid && !email) return setError('Enter a UID or Email');
    if (email && !/\S+@\S+\.\S+/.test(email)) return setError('Enter a valid email');
    try {
      setIsSubmitting(true);
      await adminApi.grantAdmin({ uid: uid.trim() || undefined, email: email.trim() || undefined });
      addToast('Admin granted successfully', 'success');
      setUid('');
      setEmail('');
      await loadAdmins();

      // If you granted admin to yourself, refresh token claims immediately
      const grantedMatchesCurrent = (
        (uid && user?.uid && uid.trim() === user.uid) ||
        (email && user?.email && email.trim().toLowerCase() === user.email.toLowerCase())
      );
      if (grantedMatchesCurrent && auth?.currentUser) {
        try {
          clearTokenCache();
          await auth.currentUser.getIdToken(true);
          // Small delay to ensure token propagation, then reload
          setTimeout(() => window.location.reload(), 400);
        } catch (_) {
          // As a fallback, suggest manual sign-out/in if needed
        }
      }
    } catch (e) {
      addToast(e.message || 'Failed to grant admin', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmRevoke = async () => {
    if (!revokeTarget) return;
    try {
      setIsSubmitting(true);
      await adminApi.revokeAdmin({ uid: revokeTarget.uid });
      addToast(`Revoked admin for ${revokeTarget.email || revokeTarget.uid}`, 'success');
      await loadAdmins();
      setRevokeTarget(null);
    } catch (e) {
      addToast(e.message || 'Failed to revoke admin', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredAdmins = useMemo(() => {
    const list = Array.isArray(admins) ? admins : [];
    return list.sort((a, b) => (a.email || '').localeCompare(b.email || ''));
  }, [admins]);

  if (!isAdmin) return null;

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center h-screen">
        <LoadingSpinner message="Loading admins..." />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 w-full max-w-[1600px] mx-auto pb-20">
      <div className="mb-4">
        <button
          onClick={() => navigate('/app/private/admin/users')}
          className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" />
          Back to User Management
        </button>
      </div>

      <div className="mb-6 sm:mb-8">
        <div className="flex items-start sm:items-center mb-4 flex-col sm:flex-row gap-2 sm:gap-0">
          <div className="flex items-center">
            <Shield className="h-5 w-5 sm:h-6 sm:w-6 text-primary-600 dark:text-primary-400 mr-2" />
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white">Admin Management</h1>
          </div>
        </div>
        <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
          Manage email addresses and UIDs that have admin access to the dashboard and tools.
        </p>
      </div>

      {error && (
        <div className="mb-4 text-sm text-red-600 dark:text-red-400">{error}</div>
      )}

      {/* Grant admin */}
      <div className="bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6 mb-6">
        <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white mb-4">Add Admin</h3>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <label htmlFor="uid" className="flex items-center text-sm text-gray-700 dark:text-gray-300 mb-1">
              <Hash className="h-4 w-4 mr-1" /> UID (optional)
            </label>
            <Input id="uid" value={uid} onChange={(e) => setUid(e.target.value)} placeholder="Firebase UID" disabled={isSubmitting} />
          </div>
          <div className="flex-1">
            <label htmlFor="email" className="flex items-center text-sm text-gray-700 dark:text-gray-300 mb-1">
              <Mail className="h-4 w-4 mr-1" /> Email (optional)
            </label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@example.com" disabled={isSubmitting} />
          </div>
          <div className="flex items-center sm:items-end justify-start sm:justify-center w-full sm:w-auto">
            <Button
              type="button"
              onClick={onGrant}
              disabled={isSubmitting || (!uid && !email)}
              className="sm:w-auto w-full"
            >
            {isSubmitting ? (
              <>
                <ButtonSpinner />
                Granting...
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" />
                Grant Admin
              </>
            )}
            </Button>
          </div>
        </div>
      </div>

      {/* Current admins */}
      <div className="bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white flex items-center">
            <Crown className="h-5 w-5 text-yellow-500 mr-2" /> Current Admins ({filteredAdmins.length})
          </h3>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">Users who currently have admin access.</p>
        </div>
        <div className="p-4 sm:p-6">
          {filteredAdmins.length === 0 ? (
            <div className="text-center py-8">
              <Shield className="h-10 w-10 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600 dark:text-gray-400">No admins found.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredAdmins.map((a) => (
                <div
                  key={a.uid}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 bg-transparent border border-gray-200 dark:border-gray-600 rounded-lg gap-3 sm:gap-0"
                >
                  <div className="flex items-start sm:items-center space-x-3 min-w-0 flex-1">
                    <div className="flex-shrink-0 mt-0.5 sm:mt-0">
                      <Crown className="h-5 w-5 text-yellow-500" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-gray-900 dark:text-white break-all">
                          {a.email || 'No email'}
                        </p>
                        {a.uid === user?.uid && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700">
                            You
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 break-all">{a.uid}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 self-end sm:self-center flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => setRevokeTarget(a)}
                      disabled={isSubmitting || a.uid === user?.uid}
                      className="p-2 text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400 transition-colors disabled:opacity-50"
                      title={a.uid === user?.uid ? 'You cannot revoke your own admin' : 'Revoke admin'}
                    >
                      {isSubmitting ? <ButtonSpinner /> : <Trash2 className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />

      <ConfirmationModal
        isOpen={!!revokeTarget}
        onClose={() => setRevokeTarget(null)}
        onConfirm={confirmRevoke}
        title="Revoke Admin Access"
        message={`Are you sure you want to revoke admin for ${revokeTarget?.email || revokeTarget?.uid}? They will immediately lose access to all admin routes.`}
        confirmButtonText="Revoke"
        isLoading={isSubmitting}
      />
    </div>
  );
}


